import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  RiskLevel,
  TriggerKnowledgeBase,
  useDeleteTrigger,
} from "@/lib/api/triggers-knowledge-base";
import { useRouter } from "@tanstack/react-router";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";

const CATEGORY_LABELS: Record<string, string> = {
  foundational: "Foundational",
  value_identification: "Value Identification",
};

export const columns: ColumnDef<TriggerKnowledgeBase>[] = [
  {
    accessorKey: "title",
    header: "Title",
    cell: ({ row }) => {
      return <div className="font-medium">{row.original.title}</div>;
    },
  },
  {
    accessorKey: "examples",
    header: "Examples",
    cell: ({ row }) => {
      const totalExamples = row.original.triggerExamples.length;
      return (
        <div className="max-w-[300px] truncate">
          {totalExamples > 1 && `${totalExamples} examples`}
        </div>
      );
    },
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: ({ row }) => {
      const category = row.original.category;
      const label = CATEGORY_LABELS[category] || category;
      return <Badge variant="outline">{label}</Badge>;
    },
  },
  {
    accessorKey: "risk_level",
    header: "Risk Level",
    cell: ({ row }) => {
      const riskLevel = row.original.risk_level;
      return (
        <Badge
          variant={
            riskLevel === RiskLevel.HIGH
              ? "destructive"
              : riskLevel === RiskLevel.MODERATE
                ? "default"
                : "secondary"
          }
        >
          {riskLevel.toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: "updated_at",
    header: "Last Updated",
    cell: ({ row }) => {
      return (
        <div className="text-sm text-gray-500">
          {format(new Date(row.original.updated_at), "MMM d, yyyy")}
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const router = useRouter();
      const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
      const { mutateAsync: deleteTrigger, isPending: isDeleting } =
        useDeleteTrigger();

      const handleDelete = async () => {
        try {
          await deleteTrigger(row.original.id);
          setShowDeleteConfirm(false);
        } catch (error) {
          // Error is already handled by the mutation (shows toast)
          // Just keep the dialog open
          console.error("Error deleting trigger:", error);
        }
      };

      return (
        <>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                router.navigate({
                  to: "/knowledge-base/$triggerKnowledgeBaseId",
                  params: { triggerKnowledgeBaseId: row.original.id },
                })
              }
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2Icon className="h-4 w-4 text-destructive" />
            </Button>
          </div>

          <ConfirmActionDialog
            isOpen={showDeleteConfirm}
            onClose={() => !isDeleting && setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title="Delete Trigger"
            description={`Are you sure you want to delete "${row.original.title}"? This action cannot be undone.`}
            loading={isDeleting}
          />
        </>
      );
    },
  },
];
